import { mapValues } from "@prismicio/editor-support/Object"
import { isDefined } from "@prismicio/editor-support/Type"
import {
  type ImageBlock,
  type ImageContent,
  type ImageContentView,
  isImageContent,
} from "@prismicio/types-internal/lib/content"
import type {
  Image,
  ImageConstraint,
} from "@prismicio/types-internal/lib/customtypes"

import { defaultImageDimension } from "../constants/images"
import type {
  MediaAsset,
  MediaAssetOrExternalImage,
} from "./MediaLibrary/hooks/mediaLibraryData"

/**
 * A thumbnail is an abstraction of the prismic models for the image fields
 * We use it to flatten the main image and its thumbnails into a single array.
 */
export interface Thumbnail {
  /** Thumbnail name or MAIN_THUMBNAIL_NAME */
  name: string
  /** Width constraint for the current thumbnail */
  width?: number | null
  /** Height constraint for the current thumbnail */
  height?: number | null
}

export const MAIN_THUMBNAIL_NAME = "__MAIN_THUMBNAIL__"

export interface CropperImage {
  thumbnail: Thumbnail
  contentView?: ImageContentView
  labels: ImageLabel
}

type ImageLabel = [thumbnailName: string | undefined, size: string | undefined]

export function getImageLabel(
  thumbnail: Thumbnail,
  content: ImageContent | ImageBlockData | undefined,
  contentView: ImageContentView | undefined,
): ImageLabel {
  const thumbnailName =
    thumbnail.name === MAIN_THUMBNAIL_NAME ? undefined : thumbnail.name

  let size: [number?, number?] | undefined
  if (!content) {
    size = undefined
  } else if (isDefined(contentView)) {
    size = [contentView.width, contentView.height]
  } else if (isDefined(thumbnail.width) || isDefined(thumbnail.height)) {
    size = [thumbnail.width ?? undefined, thumbnail.height ?? undefined]
  } else {
    size = [content.origin.width, content.origin.height]
  }

  return [
    thumbnailName,
    size ? `${size[0] ?? "auto"}×${size[1] ?? "auto"}px` : undefined,
  ]
}

export function getImageThumbnails(field: Image): readonly Thumbnail[] {
  return [{ ...field.config?.constraint, name: MAIN_THUMBNAIL_NAME }].concat(
    field.config?.thumbnails ?? [],
  )
}

export function getImageContentViews(
  field: Image,
  content: ImageContent,
): readonly [ImageContentView, ...(ImageContentView | undefined)[]] {
  const { __TYPE__, thumbnails, ...headContentView } = content
  const tailContentViews = field.config?.thumbnails?.map(
    ({ name }) => thumbnails?.[name],
  )
  return [headContentView, ...(tailContentViews ?? [])]
}

export function updateImageContentAlt<
  ContentType extends ImageContent | ImageBlockData,
>(
  thumbnailName: string,
  content: ContentType,
  contentView: ImageContentView,
  value: string,
): ContentType {
  const alt = value.length === 0 ? undefined : value
  /*
   * The main image thumbnail alt text is defined at the root of the content.
   * An image in rich text doesn't have thumbnails, so we only update the main image alt.
   */
  return thumbnailName === MAIN_THUMBNAIL_NAME || !isImageContent(content)
    ? { ...content, alt }
    : {
        ...content,
        thumbnails: {
          ...content.thumbnails,
          [thumbnailName]: { ...contentView, alt },
        },
      }
}

export function getConstraints(
  extImage: { width: number; height: number },
  constraints?: ImageConstraint,
): [number, number] {
  const { width, height } = constraints ?? {}

  // If we have both constraints, keep them.
  if (width && height) return [width, height]

  // If we only have a width constraint, calculate the missing height constraint based on the image ratio.
  if (width)
    return [width, Math.round(extImage.height * (width / extImage.width))]

  // If we only have a height constraint, calculate the missing width constraint based on the image ratio.
  if (height)
    return [Math.round(extImage.width * (height / extImage.height)), height]

  // If we have no constraints at all, then use the original size.
  return [extImage.width, extImage.height]
}

export function buildImageUrl(
  originUrl: string,
  rect: { x: number; y: number; width: number; height: number },
  outputWidth: number,
  outputHeight: number,
  hasConstraints: boolean,
) {
  // svg files are not compatible with imgx transformations.
  if (getMediaUrlExtension(originUrl) === "svg") return originUrl

  const url = new URL(originUrl)

  // No need to needlessly complexify the URL if there are no constraints.
  // Also, we don't use url.searchParams as it returns parsed params but we want to keep the stored URL clean and readable
  // as all imgx query params are URL-safe.
  if (hasConstraints) {
    const { x, y, width, height } = rect

    const urlParams = [
      ...url.searchParams.entries(),
      ["rect", [x, y, width, height].join(",")],
      ["w", outputWidth],
      ["h", outputHeight],
    ]

    url.search = urlParams.map((pair) => pair.join("=")).join("&")
  }

  return url.toString()
}

/*
 * Returns an image that fits the given width and height constraints.
 */
export function getPreviewImage(
  fromUrl: string,
  options?: { width?: number; height?: number },
) {
  const url = new URL(fromUrl.split("?")[0] ?? fromUrl)

  // See: https://docs.imgix.com/en-US/apis/rendering/size/image-width
  if (options?.width) {
    url.searchParams.set("w", options.width.toString())
  }

  // See: https://docs.imgix.com/en-US/apis/rendering/size/image-height
  if (options?.height) {
    url.searchParams.set("h", options.height.toString())
  }

  // See: https://docs.imgix.com/en-US/apis/rendering/device-pixel-ratio
  if (options?.width || !options?.height) {
    url.searchParams.set(
      "dpr",
      Math.max(1, Math.min(window.devicePixelRatio, 3)).toString(),
    )
  }

  // See: https://docs.imgix.com/en-US/apis/rendering/size/resize-fit-mode#max
  if (options?.width && options?.height) {
    url.searchParams.set("fit", "max")
  }

  // See: https://docs.imgix.com/en-US/apis/rendering/automatic
  url.searchParams.set("auto", "compress,format")

  return url.toString()
}

/*
 * Returns an image that looks decent in the search results
 * but is lighter than the typical "small" default URL.
 */
export function getSearchTileImage(fromUrl: string) {
  return getPreviewImage(fromUrl, { width: 300 })
}

/*
 * Returns an image that looks good in the carousel
 * but is lighter than the typical main/thumbnail image.
 */
export function getCarouselImage(fromUrl: string) {
  return getPreviewImage(fromUrl, { width: 800, height: 800 })
}

export function getCarouselItemSource(view: ImageContentView | undefined) {
  return view?.url ? getCarouselImage(view.url) : undefined
}

export function replaceImage(
  thumbnailName: string,
  field: Image,
  content: ImageContent | undefined,
  extImage: MediaAssetOrExternalImage,
): ImageContent {
  // Brand new image content or main image replace
  // Fill the main image but also all the thumbnails.
  if (!content || thumbnailName === MAIN_THUMBNAIL_NAME) {
    const thumbnailsModel = field.config?.thumbnails

    const thumbnails =
      thumbnailsModel &&
      Object.fromEntries(
        thumbnailsModel.map((model) => [
          model.name,
          createContentView(extImage, model),
        ]),
      )

    return {
      __TYPE__: "ImageContent",
      ...createContentView(extImage, field.config?.constraint),
      thumbnails,
    }
  }

  // Updating an existing thumbnail, if it still exists in the model.
  const model = field.config?.thumbnails?.find((t) => t.name === thumbnailName)

  if (!model) return content

  const currentThumbnail = content.thumbnails?.[thumbnailName]
  const updatedThumbnail = createContentView(extImage, model)

  if (updatedThumbnail.url === currentThumbnail?.url) return content

  return {
    ...content,
    thumbnails: mapValues(content.thumbnails ?? {}, (thumbnail, name) =>
      name === thumbnailName ? updatedThumbnail : thumbnail,
    ),
  }
}

export function isMediaAsset(
  image: MediaAssetOrExternalImage,
): image is MediaAsset {
  return "kind" in image && "extension" in image
}

export function createContentView(
  image: MediaAssetOrExternalImage,
  constraints?: ImageConstraint,
): ImageContentView {
  const { width, height, rect } = fitImage(
    {
      ...image,
      width: image?.width ?? defaultImageDimension,
      height: image?.height ?? defaultImageDimension,
    },
    constraints,
  )

  const hasConstraints = Boolean(constraints?.width || constraints?.height)
  const url = buildImageUrl(image.url, rect, width, height, hasConstraints)

  return {
    provider: "imgix",
    url,
    alt: image.alt,
    ...(isMediaAsset(image) ? { credits: image.credits } : {}),
    origin: {
      id: image.id,
      url: image.url,
      width: image.width ?? defaultImageDimension,
      height: image.height ?? defaultImageDimension,
    },
    width,
    height,
    edit: {
      background: "transparent",
      zoom: 1,
      crop: {
        x: rect.x,
        y: rect.y,
      },
    },
  }
}

/* Computes the best size and source rect for the image that satisfies the model constraints. */
export function fitImage(
  extImage: { width: number; height: number },
  constraints?: ImageConstraint,
) {
  const [constraintWidth, constraintHeight] = getConstraints(
    extImage,
    constraints,
  )

  // Computes which side is the most constraining so we can calculate the most fitting source rectangle.
  const scaleFactor = Math.max(
    constraintWidth / extImage.width,
    constraintHeight / extImage.height,
  )
  const rectX = Math.round((extImage.width - constraintWidth / scaleFactor) / 2)
  const rectY = Math.round(
    (extImage.height - constraintHeight / scaleFactor) / 2,
  )
  const rectWidth = Math.round(constraintWidth / scaleFactor)
  const rectHeight = Math.round(constraintHeight / scaleFactor)

  return {
    width: constraintWidth,
    height: constraintHeight,
    rect: {
      x: rectX,
      y: rectY,
      width: rectWidth,
      height: rectHeight,
    },
  }
}

export function getMediaUrlExtension(url: string): string | undefined {
  const { pathname } = new URL(url)

  const lastDotIndex = pathname.lastIndexOf(".")
  if (lastDotIndex === -1) return undefined

  return pathname.substring(lastDotIndex).slice(1).toLowerCase()
}

export function getTitleWithoutExtension(selectedMedia?: MediaAsset) {
  if (!selectedMedia) return ""

  const { filename, extension } = selectedMedia

  if (!extension || !filename.endsWith(`.${extension}`)) {
    return filename
  }

  return filename.slice(0, -extension.length - 1)
}

export type ImageBlockData = ImageBlock["data"]
